// ============================================================================
// Local Sync corpus naming helpers
// ----------------------------------------------------------------------------
// Local Sync is the processed QuoteMaker corpus, not the raw RFQ evidence
// archive. Folder names should be readable in Explorer and stable enough for
// recovery. Keep these helpers pure so tests do not need File System Access API.
// ============================================================================

const MAX_SEGMENT_LENGTH = 42;
const MAX_FOLDER_LENGTH = 170;

const STAGE_LABELS: Record<number, string> = {
    1: 'Stage 1 Entry',
    2: 'Stage 2 Risk Report',
    3: 'Stage 3 Work Method',
    4: 'Stage 4 Scope Builder',
    5: 'Stage 5 Final Quote',
};

function stripHtml(value: unknown): string {
    return String(value || '').replace(/<[^>]*>/g, ' ');
}

export function sanitizeSyncSegment(value: unknown, maxLength = MAX_SEGMENT_LENGTH): string {
    const text = stripHtml(value)
        .replace(/[<>:"/\\|?*\x00-\x1f]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/[. ]+$/g, '');
    if (!text) return '';
    return text.length > maxLength ? text.slice(0, maxLength).trim().replace(/[. ]+$/g, '') : text;
}

function isoDateFrom(value: unknown): string {
    if (!value) return '';
    const date = new Date(value as string | number | Date);
    if (Number.isNaN(date.getTime())) return '';
    return date.toISOString().slice(0, 10);
}

function shortProjectId(projectRecord: Record<string, any> = {}): string {
    const id = String(projectRecord.id || projectRecord.dexieId || projectRecord.projectId || '').trim();
    if (!id) return '';
    const compact = id.replace(/^proj[_-]/i, '').replace(/[^a-z0-9]/gi, '');
    return compact ? `P ${compact.slice(0, 8)}` : '';
}

function quoteRefSegment(formData: Record<string, any> = {}, projectRecord: Record<string, any> = {}): string {
    const raw = sanitizeSyncSegment(
        formData.orderNumber
        || projectRecord.quoteNumber
        || projectRecord.quoteData?.meta?.quoteNumber
        || '',
        30
    );
    if (!raw) return '';
    return /^(q\.?\s*r|qr|quote)/i.test(raw) ? raw : `QR ${raw}`;
}

function requesterSegment(formData: Record<string, any> = {}, projectRecord: Record<string, any> = {}): string {
    return sanitizeSyncSegment(
        formData.requesterCompany
        || formData.clientCompany
        || formData.clientName
        || projectRecord.requesterCompany
        || projectRecord.clientCompany
        || projectRecord.companyName
        || '',
        38
    );
}

function strataSegment(formData: Record<string, any> = {}): string {
    const raw = sanitizeSyncSegment(formData.strataplan || formData.strataPlan || '', 22);
    if (!raw) return '';
    return /^sp[\s._-]?\d+/i.test(raw) ? raw.toUpperCase().replace(/\s+/g, ' ') : `SP ${raw}`;
}

function addressSegment(formData: Record<string, any> = {}, projectRecord: Record<string, any> = {}): string {
    const raw = formData.address || projectRecord.address || projectRecord.title || 'Unaddressed Project';
    return sanitizeSyncSegment(
        String(raw)
            .replace(/\bNSW\b/gi, '')
            .replace(/\bAustralia\b/gi, '')
            .replace(/,\s*/g, ' '),
        58
    ) || 'Unaddressed Project';
}

function compactFolderName(parts: string[]): string {
    const joined = parts.filter(Boolean).join(' - ');
    if (joined.length <= MAX_FOLDER_LENGTH) return joined;
    const requiredTail = parts[parts.length - 1] || '';
    const room = Math.max(80, MAX_FOLDER_LENGTH - requiredTail.length - 3);
    return `${joined.slice(0, room).trim().replace(/[. -]+$/g, '')} - ${requiredTail}`.slice(0, MAX_FOLDER_LENGTH).trim();
}

export function buildSyncFolderName(projectRecord: Record<string, any> = {}): string {
    const formData = projectRecord.formData || {};
    const date = isoDateFrom(projectRecord.created_at || projectRecord.createdAt || projectRecord.updated_at) || isoDateFrom(Date.now());
    const parts = [
        date,
        quoteRefSegment(formData, projectRecord),
        requesterSegment(formData, projectRecord),
        strataSegment(formData),
        addressSegment(formData, projectRecord),
        shortProjectId(projectRecord),
    ];
    return compactFolderName(parts) || 'QuoteMaker Project';
}

function numberFrom(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    const parsed = Number.parseFloat(String(value || '').replace(/[$,\s]/g, ''));
    return Number.isFinite(parsed) ? parsed : null;
}

function formatMoney(value: unknown): string {
    const n = numberFrom(value);
    if (n === null) return '';
    return `$${Math.round(n).toLocaleString('en-AU')}`;
}

function quoteTotal(projectRecord: Record<string, any> = {}): string {
    const quoteData = projectRecord.quoteData || {};
    const direct = quoteData.summary?.total
        ?? quoteData.summary?.totalIncGst
        ?? quoteData.meta?.total
        ?? quoteData.totalIncGst
        ?? projectRecord.totalIncGst;
    const directMoney = formatMoney(direct);
    if (directMoney) return directMoney;
    const scope = Array.isArray(projectRecord.scope) ? projectRecord.scope : [];
    const total = scope.reduce((sum, item) => sum + (numberFrom(item.amount ?? item.amountIncGst) || 0), 0);
    return total > 0 ? formatMoney(total) : '';
}

export function buildProjectIndexMarkdown({
    projectRecord = {},
    folderName = '',
    photos = [],
    documents = [],
    videos = [],
    results = {},
}: {
    projectRecord?: Record<string, any>;
    folderName?: string;
    photos?: any[];
    documents?: any[];
    videos?: any[];
    results?: Record<string, any>;
} = {}): string {
    const fd = projectRecord.formData || {};
    const title = sanitizeSyncSegment(projectRecord.title || fd.address || 'Untitled Project', 120);
    const total = quoteTotal(projectRecord);
    const keyDetails = [
        fd.orderNumber || projectRecord.quoteNumber ? `- Quote / order ref: ${fd.orderNumber || projectRecord.quoteNumber}` : '',
        fd.requesterCompany || fd.clientCompany ? `- Requester company: ${fd.requesterCompany || fd.clientCompany}` : '',
        fd.clientName ? `- Contact: ${fd.clientName}` : '',
        fd.address || projectRecord.address ? `- Site address: ${fd.address || projectRecord.address}` : '',
        fd.strataplan || fd.strataPlan ? `- Strata plan: ${fd.strataplan || fd.strataPlan}` : '',
        projectRecord.status ? `- Status: ${projectRecord.status}` : '',
        projectRecord.currentStage ? `- Current stage: ${STAGE_LABELS[projectRecord.currentStage] || `Stage ${projectRecord.currentStage}`}` : '',
        total ? `- Quote total inc GST: ${total}` : '',
    ].filter(Boolean);
    const lines = [
        '# QuoteMaker Project Index',
        '',
        `Project: ${title}`,
        ...(folderName ? [`Folder: ${folderName}`] : []),
        `Synced: ${new Date().toISOString()}`,
        '',
        '## Key Details',
        '',
        ...(keyDetails.length ? keyDetails : ['- No key details recorded yet.']),
        '',
        '## Corpus Contents',
        '',
        `- Photos: ${Array.isArray(photos) ? photos.length : 0}`,
        `- Documents: ${Array.isArray(documents) ? documents.length : 0}`,
        `- Videos: ${Array.isArray(videos) ? videos.length : 0}`,
        `- Scope items: ${Array.isArray(projectRecord.scope) ? projectRecord.scope.length : 0}`,
        '',
        '## Folder Map',
        '',
        '- `QuoteData.json` machine-readable QuoteMaker project snapshot (includes documents[].content extracted text)',
        '- `SyncManifest.json` machine-readable file inventory for restore (schema v5)',
        '- `Inspector_Notes.txt` site/import notes when available',
        '- `Risk_Report.txt` Stage 2 risk report when available',
        '- `AI_Learnings.json` project-relevant AI learning snapshot when available',
        '',
        '_v5.37.9 de-bloat: source photos / documents / videos are no longer duplicated_',
        '_into the project folder. Originals live where the user put them (intake_',
        '_folder / FileExplorer source). Restore on a fresh device needs originals._',
        '',
        '## Sync Result',
        '',
        `- QuoteData written: ${results.quote ? 'yes' : 'no'}`,
        `- Notes written: ${results.notes ? 'yes' : 'no'}`,
        `- Risk report written: ${results.riskReport ? 'yes' : 'no'}`,
        `- Learnings written: ${results.learnings ? 'yes' : 'no'}`,
        '',
        'This folder is a QuoteMaker-processed corpus record. Keep raw RFQ source folders separate unless you intentionally want to archive generated QuoteMaker outputs beside them.',
        '',
    ];
    return lines.join('\n');
}
